// HOME del cockpit — primera vista al entrar. Reutilizado para clínica (MedOS)
// y para eventos/servicios (events-services): mismo backend, distinta
// terminología (archetype-aware vía cockpitLabels).
// 4 widgets: Agenda + Alertas + Pendientes + Métricas + Cotizaciones enviadas.

import { useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, Calendar, Clock, FileSignature,
  MessageSquare, PartyPopper, Stethoscope, TrendingUp, User2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useModules } from '@/context/ModulesContext';
import { appointmentsSummary, listAppointments, listPatients, listQuotes } from '../api';
import { cockpitLabels } from '../labels';
import type { AppointmentOut, AppointmentsSummaryOut, PatientSummary, QuoteListItem } from '../types';

function todayISO(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Buen día';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

export function ClinicalHomeView({ doctorName = 'Doctor' }: { doctorName?: string }) {
  const [summary, setSummary] = useState<AppointmentsSummaryOut | null>(null);
  const [todayAppts, setTodayAppts] = useState<AppointmentOut[]>([]);
  const [alertPatients, setAlertPatients] = useState<PatientSummary[]>([]);
  const [quotes, setQuotes] = useState<QuoteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { archetype } = useModules();
  const L = cockpitLabels(archetype);

  // Cotizaciones enviadas — fetch aparte (best-effort): si el endpoint aún no
  // está desplegado o falla, NO rompe el home; solo no muestra la sección.
  useEffect(() => {
    let cancel = false;
    listQuotes(undefined, 20)
      .then((q) => { if (!cancel) setQuotes(q); })
      .catch(() => { /* sin cotizaciones o endpoint no listo → sección oculta */ });
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const today = todayISO();
    Promise.all([
      appointmentsSummary(),
      listAppointments({ fromDate: today, toDate: today, limit: 50 }),
      listPatients('', 100),
    ])
      .then(([s, a, p]) => {
        if (cancel) return;
        setSummary(s);
        setTodayAppts(a);
        setAlertPatients(p.filter((pt) => pt.has_alerts));
      })
      .catch((e) => { if (!cancel) setError(String(e?.message || e)); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const completedToday = useMemo(
    () => todayAppts.filter((a) => a.status === 'completada').length,
    [todayAppts],
  );
  const pendingToday = useMemo(
    () => todayAppts.filter((a) => a.status === 'programada' || a.status === 'en_curso').length,
    [todayAppts],
  );

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{L.loading}</div>;
  }
  if (error) {
    return <div className="p-6 text-sm text-destructive">Error: {error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header de saludo */}
      <header className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-3">
          {L.events
            ? <PartyPopper size={28} className="text-primary" />
            : <Stethoscope size={28} className="text-primary" />}
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-foreground">
              {greeting()}{L.events ? '' : `, ${doctorName}`}
            </h1>
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString('es-MX', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              })}
              {' · '}
              {pendingToday > 0
                ? `${pendingToday} pendiente${pendingToday > 1 ? 's' : ''} hoy`
                : 'Sin pendientes hoy'}
            </p>
          </div>
        </div>
      </header>

      {/* 4 widgets — grid 2x2 en desktop, stack en mobile */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Widget AGENDA */}
        <Widget
          title="Agenda de hoy"
          icon={<Calendar size={16} />}
          ctaLabel="Ver agenda completa"
          onCta={() => navigate('/m/clinical-cockpit/agenda')}
        >
          {todayAppts.length === 0 ? (
            <p className="text-sm text-muted-foreground">{L.noAgenda}</p>
          ) : (
            <ul className="space-y-1.5">
              {todayAppts.slice(0, 5).map((a) => (
                <li key={a.id} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">{fmtTime(a.scheduled_at)}</span>
                  <span className="font-medium text-foreground truncate flex-1">{a.patient_name}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{a.type}</span>
                </li>
              ))}
              {todayAppts.length > 5 && (
                <li className="text-xs text-muted-foreground italic">
                  +{todayAppts.length - 5} más…
                </li>
              )}
            </ul>
          )}
        </Widget>

        {/* Widget ALERTAS */}
        <Widget
          title={L.clientsAlerts}
          icon={<AlertTriangle size={16} className="text-amber-400" />}
          ctaLabel="Ver todos"
          onCta={() => navigate('/m/clinical-cockpit/patients')}
        >
          {alertPatients.length === 0 ? (
            <p className="text-sm text-muted-foreground">{L.noAlerts}</p>
          ) : (
            <ul className="space-y-1.5">
              {alertPatients.slice(0, 5).map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-sm">
                  <AlertTriangle size={12} className="shrink-0 text-amber-400" />
                  <span className="font-medium text-foreground truncate flex-1">{p.display_name}</span>
                  {p.age !== null && (
                    <span className="text-xs text-muted-foreground">{p.age}a</span>
                  )}
                </li>
              ))}
              {alertPatients.length > 5 && (
                <li className="text-xs text-muted-foreground italic">
                  +{alertPatients.length - 5} más…
                </li>
              )}
            </ul>
          )}
        </Widget>

        {/* Widget PENDIENTES */}
        <Widget
          title="Pendientes"
          icon={<FileSignature size={16} className="text-blue-400" />}
        >
          <ul className="space-y-2 text-sm">
            <PendingItem
              icon={<FileSignature size={14} className="text-muted-foreground" />}
              label={L.pendingRecords}
              count={completedToday}
              hint={completedToday > 0 ? L.pendingRecordsHint : 'al día'}
            />
            <PendingItem
              icon={<MessageSquare size={14} className="text-muted-foreground" />}
              label="Mensajes sin atender"
              count={0}
              hint="WhatsApp + correo en bandeja"
            />
            <PendingItem
              icon={<User2 size={14} className="text-muted-foreground" />}
              label={L.newClients}
              count={summary?.patients_unique_week ?? 0}
              hint="únicos con cita programada"
            />
          </ul>
        </Widget>

        {/* Widget MÉTRICAS */}
        <Widget
          title="Métricas de la semana"
          icon={<TrendingUp size={16} className="text-emerald-400" />}
        >
          {summary ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Metric label={L.metricMain} value={summary.week_count} icon={<Calendar size={12} />} />
              <Metric label={L.metricUnique} value={summary.patients_unique_week} icon={<User2 size={12} />} />
              <Metric label="Hoy" value={summary.today_count} icon={<Clock size={12} />} />
              <Metric
                label="No-shows (30d)"
                value={summary.no_shows_30d}
                icon={<Activity size={12} />}
                tone={summary.no_shows_30d > 3 ? 'warn' : undefined}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin datos suficientes.</p>
          )}
        </Widget>
      </div>

      {/* Cotizaciones enviadas — aparece cuando hay (best-effort). */}
      {quotes.length > 0 && (
        <section className="rounded-lg border border-border bg-card p-4">
          <header className="mb-3 flex items-center gap-2">
            <FileSignature size={16} className="text-primary" />
            <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Cotizaciones enviadas
            </h2>
          </header>
          <ul className="divide-y divide-border">
            {quotes.slice(0, 10).map((q) => (
              <li key={q.id} className="flex items-center gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{q.patient_name}</span>
                <span className="hidden min-w-0 flex-1 truncate text-muted-foreground sm:block">
                  {q.items_summary}
                </span>
                <span className="shrink-0 font-semibold text-foreground">
                  ${q.total_mxn.toLocaleString('es-MX')}
                </span>
                <span
                  className={`shrink-0 rounded px-2 py-0.5 text-[11px] ${
                    q.status === 'sent'
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {q.status === 'sent' ? 'Enviada' : q.status === 'pending_send' ? 'Enviando…' : q.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Asistente IA hint */}
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-medium text-foreground">
          {L.events ? <PartyPopper size={14} /> : <Stethoscope size={14} />} {L.assistantTitle}
        </h3>
        <p className="text-sm text-muted-foreground">{L.assistantHint}</p>
      </div>
    </div>
  );
}

// ── Sub-componentes ───────────────────────────────────────

function Widget({
  title, icon, ctaLabel, onCta, children,
}: {
  title: string;
  icon: React.ReactNode;
  ctaLabel?: string;
  onCta?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {icon} {title}
        </h2>
        {ctaLabel && onCta && (
          <button
            type="button"
            onClick={onCta}
            className="text-xs text-primary hover:underline"
          >
            {ctaLabel} →
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function PendingItem({ icon, label, count, hint }: {
  icon: React.ReactNode;
  label: string;
  count: number;
  hint: string;
}) {
  return (
    <li className="flex items-center gap-2">
      {icon}
      <span className="flex-1 min-w-0">
        <span className="block text-foreground">{label}</span>
        {hint && <span className="block text-[11px] text-muted-foreground truncate">{hint}</span>}
      </span>
      <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${
        count > 0 ? 'bg-amber-500/15 text-amber-400' : 'bg-muted text-muted-foreground'
      }`}>
        {count}
      </span>
    </li>
  );
}

function Metric({ label, value, icon, tone }: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: 'warn';
}) {
  const toneCls = tone === 'warn' ? 'text-amber-400' : 'text-foreground';
  return (
    <div className="rounded border border-border bg-background p-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>{icon}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

export default ClinicalHomeView;
